import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import Label from "@components/inputs/text/Label";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { NavBar } from "@components/navigation/NavBar/NavBar";
import { InfoBox } from "@components/cards/messageBox";
import { IPricingRecord } from "@services/QuoteBuilder/QuoteBuilderService";
import { PricingRecordAction } from "./pricingRecordReducer";
import PlanOptionComponent from "./PlanOptionComponent";
import { IPricingRecordLineItem } from "@services/QuoteBuilder/QuoteBuilderService";
import { PanelItem } from "@components/panels/PanelItems";
import Checkbox from "@components/inputs/Checkbox";

type Props = {
    pricingRecordState: IPricingRecord | null;
    pricingRecordDispatch: React.Dispatch<PricingRecordAction>;
    navigateLandingPageTarget: string;
    navigateNextTarget: string;
};

export default function DiscountOptionsView({
    pricingRecordState,
    pricingRecordDispatch,
    navigateLandingPageTarget,
    navigateNextTarget,
}: Props) {
    const dynamicPricingDiscountComponentList: string[] =
        pricingRecordState?.component_lists?.dynamic_pricing
            ? pricingRecordState?.component_lists?.dynamic_pricing
            : []
        ;

    const ignitionDiscountComponentList: string[] =
        pricingRecordState?.component_lists?.ignition
            ? pricingRecordState?.component_lists?.ignition
            : []
        ;

    const ignitionOptionLineItems = pricingRecordState?.pricing_record_line_items.filter(
        (item: IPricingRecordLineItem) =>
            ignitionDiscountComponentList.includes(item.chargebee_component)
    );

    const navigate = useNavigate();

    const msalContext = useMsal();
    const userDetails = msalContext.instance.getActiveAccount();

    useEffect(() => {
        if (pricingRecordState === null) {
            navigate(navigateLandingPageTarget);
        }
    }, [pricingRecordState, navigateLandingPageTarget, navigate]);

    const handleCheckboxChange = () => {
        const localIsChecked =
            !(pricingRecordState?.trip_count_reviewed_by
                ? true
                : false);
        let loginUser: string | undefined = "";

        // Set the user name when checkbox is checked
        if (localIsChecked) {
            loginUser = userDetails?.name;
        }
        pricingRecordDispatch({
            type: "UPDATE_FIELD",
            payload: {
                field: "trip_count_reviewed_by",
                value: loginUser,
            },
        });
    };

    /**
     * On submit handler
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        navigate(navigateNextTarget);
    };

    return (
        <>
            <NavBar pageTitle="Discount Options" />
            <form style={{ margin: "2.5vh 2.5vw" }} onSubmit={handleSubmit}>
                <InfoBox
                    testId={"InfoBox-Discount"}
                    message={
                        <>
                            The available Discount options are determined by the Initiative selected for the
                            Potential. Only Dynamic Pricing and Ignition can be applied at the quote level.
                        </>
                    }
                />
                {
                    pricingRecordState?.splend_initiative &&
                    pricingRecordState?.splend_initiative === 'Dynamic Pricing' &&
                    <>
                        <div style={{ margin: "1vh 0vw" }}></div>
                        <Label text="Dynamic Pricing" styleCfg={{ bold: true }} />
                        <div style={{ margin: "1vh 0vw" }}></div>
                        <Label text="Select the Total Weekly Discount:" />
                        <div style={{ margin: "1vh 0 2.5vh 0" }}>
                            <PlanOptionComponent
                                name="DiscountOption-DynamicPricing"
                                pricingRecordState={pricingRecordState}
                                pricingRecordDispatch={pricingRecordDispatch}
                                optionList={dynamicPricingDiscountComponentList}
                                noneOption={{ label: "None", value: "", enabled: true }}
                                required={true}
                            />
                        </div>
                    </>
                }

                {
                    pricingRecordState?.splend_initiative &&
                    pricingRecordState?.splend_initiative === 'Ignition' &&
                    <>
                        <div style={{ margin: "1vh 0vw" }}></div>
                        <Label text="Ignition" styleCfg={{ bold: true }} />
                        <div style={{ margin: "1vh 0 2.5vh 0" }}>
                            {
                                ignitionOptionLineItems ?
                                    (
                                        ignitionOptionLineItems.
                                            filter((item) => item.included).
                                            map((item, index: number) => (
                                                <PanelItem
                                                    key={index}
                                                    header={item.line_item_label_name}
                                                    value={item.price_including_gst.toFixed(2)}
                                                    headerTextStyleCfg={{ lineBreak: true }}
                                                    testId={`$DiscountOption-Ignition-selected-${item.line_item_label_name}`}
                                                />
                                            ))
                                    )
                                    : undefined
                            }

                            {/*
                            <PlanOptionComponent
                                name="DiscountOption-Ignition"
                                pricingRecordState={pricingRecordState}
                                pricingRecordDispatch={pricingRecordDispatch}
                                optionList={ignitionDiscountComponentList}
                                noneOption={{ label: "None", value: "", enabled: true }}
                                required={true}
                            />
                            */}
                        </div>
                    </>
                }

                {
                    pricingRecordState?.splend_initiative &&
                    ['Dynamic Pricing', 'Ignition'].includes(pricingRecordState?.splend_initiative) &&
                    <Checkbox
                        label={`I, ${userDetails?.name}, declare that I have reviewed the customer's Uber trip and have followed prescribed process.`}
                        onChange={handleCheckboxChange}
                        id="confirmed"
                        name="trip_count_reviewed_by"
                        required={true}
                        checked={
                            pricingRecordState?.trip_count_reviewed_by
                                ? true
                                : false
                        }
                        testId="TripCountVerifiedBy-Checkbox"
                    />
                }

                {
                    (
                        !pricingRecordState?.splend_initiative ||
                        !(['Dynamic Pricing', 'Ignition'].includes(pricingRecordState?.splend_initiative))
                    ) &&
                    <div style={{ margin: "1vh 0vw" }}>
                        No Discount applied
                    </div>
                }

                <div style={{ margin: "2.5vh 0" }}>
                    <SubmitBtn text="Next" testId="submit-button"/>
                </div>
            </form>
        </>
    )
}